import { useCallback, useMemo } from 'react';
import {
  toOpsPartners,
  waitingByPartner,
  watchBoxesHere,
  watchConfigList,
  watchHandover,
  watchHandovers,
  watchPartnerShipmentsAtHub,
  type BoxRow,
  type ConfigEntry,
  type HandoverRow,
  type OpsPartner,
  type ShipmentRow,
  type WaitingUnit,
} from '@ecobox/firebase';
import { useServices } from '../services';
import { useWatch, type Watched } from './useWatch';

const NONE: never[] = [];

/** Active haulers and couriers: who a handover can be opened for. */
export function usePartners(): Watched<OpsPartner[]> & { raw: ConfigEntry<'partner'>[] } {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: ConfigEntry<'partner'>[]) => void, e: (err: Error) => void) =>
      watchConfigList(db, 'partner', d, e),
    [db],
  );
  const raw = useWatch(start, NONE as ConfigEntry<'partner'>[]);
  const data = useMemo(() => toOpsPartners(raw.data), [raw.data]);
  return { data, loading: raw.loading, error: raw.error, raw: raw.data };
}

/** Handovers at my branch that have not been signed yet, so a handover started on another phone can be picked up. */
export function useOpenHandovers(branchId: string | null): Watched<HandoverRow[]> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: HandoverRow[]) => void, e: (err: Error) => void) =>
      watchHandovers(
        db,
        { scope: { branchId: branchId ?? '' }, statuses: ['draft', 'loading'], max: 20 },
        d,
        e,
      ),
    [db, branchId],
  );
  return useWatch(branchId ? start : null, NONE as HandoverRow[]);
}

/** One handover, live: its status and counts as the server has them. */
export function useHandover(handoverId: string | null): Watched<HandoverRow | null> {
  const { db } = useServices().client;
  const start = useCallback(
    (d: (v: HandoverRow | null) => void, e: (err: Error) => void) =>
      watchHandover(db, handoverId ?? '', d, e),
    [db, handoverId],
  );
  return useWatch(handoverId ? start : null, null as HandoverRow | null);
}

export interface WaitingState {
  /** Units waiting at this hub, by the partner the coverage map names. */
  byPartner: ReadonlyMap<string, readonly WaitingUnit[]>;
  /** Boxes at the hub, by barcode (for the warning before the server speaks). */
  boxes: ReadonlyMap<string, BoxRow>;
  ready: boolean;
  /** Why the list could not be built. Scanning still works. */
  error: string | null;
}

/**
 * What is waiting at the hub for each partner: the hub's boxes, the partner-mode shipments they
 * belong to, and the coverage map, joined on the phone. Firestore keeps all three in its offline
 * cache, so the list works with no signal once it has been read.
 */
export function useWaitingUnits(branchId: string | null): WaitingState {
  const { db } = useServices().client;
  const startBoxes = useCallback(
    (d: (v: BoxRow[]) => void, e: (err: Error) => void) =>
      watchBoxesHere(db, { branchId: branchId ?? '' }, d, e),
    [db, branchId],
  );
  const startShipments = useCallback(
    (d: (v: ShipmentRow[]) => void, e: (err: Error) => void) =>
      watchPartnerShipmentsAtHub(db, { hubId: branchId ?? '' }, d, e),
    [db, branchId],
  );
  const startCoverage = useCallback(
    (d: (v: ConfigEntry<'coverage'>[]) => void, e: (err: Error) => void) =>
      watchConfigList(db, 'coverage', d, e),
    [db],
  );
  const live = branchId !== null;
  const boxes = useWatch(live ? startBoxes : null, NONE as BoxRow[]);
  const shipments = useWatch(live ? startShipments : null, NONE as ShipmentRow[]);
  const coverage = useWatch(live ? startCoverage : null, NONE as ConfigEntry<'coverage'>[]);
  const boxMap = useMemo(() => new Map(boxes.data.map((b) => [b.barcode, b])), [boxes.data]);
  const byPartner = useMemo(
    () =>
      waitingByPartner({ boxes: boxes.data, shipments: shipments.data, coverage: coverage.data }),
    [boxes.data, shipments.data, coverage.data],
  );
  return {
    byPartner,
    boxes: boxMap,
    ready: !boxes.loading && !shipments.loading && !coverage.loading,
    error: shipments.error ?? coverage.error ?? boxes.error,
  };
}
